import { createFileRoute } from '@tanstack/react-router';
import { fetchCvp } from '@/features/travel-credit-card/cbcc/services/fetch-cvp';
import { resolveUserFlow } from '@/features/travel-credit-card/cbcc/utils/resolve-user-flow';
import { resolveDevice } from '@/features/travel-credit-card/constants/device';
import { ASSETS } from '@/features/travel-credit-card/content/assets';
import { SEO_CONTENT } from '@/features/travel-credit-card/content/content';
import { cbccStructuredDataJson } from '@/features/travel-credit-card/content/structured-data';
import {
	ForceLightScheme,
	forceLightSchemeScript,
} from '@/features/travel-credit-card/layout/force-light-scheme';
import { TravelCreditCardLayout } from '@/features/travel-credit-card/layout/travel-credit-card-layout';
import { getAuth } from '@/lib/login/auth';
import { seo } from '@/utils/seo';

export const Route = createFileRoute('/travel-credit-card')({
	head: () => ({
		meta: [
			...seo({
				title: SEO_CONTENT.title,
				description: SEO_CONTENT.description,
				image: SEO_CONTENT.ogImage || undefined,
				url: SEO_CONTENT.canonicalUrl,
				largeImage: true,
			}),
			// Override the root route's `noindex,nofollow` — this is a public,
			// crawlable marketing page.
			{ name: 'robots', content: 'index, follow' },
		],
		links: [
			{ rel: 'canonical', href: SEO_CONTENT.canonicalUrl },
			{ rel: 'icon', href: SEO_CONTENT.icon },
			// Preload the LCP hero background so it's discoverable in the initial
			// document and fetched at high priority (Lighthouse: LCP 4.1s). Two
			// media-scoped preloads: the web/desktop hero uses heroBackground, the
			// mweb hero uses heroBackgroundMobile — the browser fetches ONLY the one
			// matching the viewport, so neither device wastes bytes on the other's
			// image (the render itself is UA-based; the ~768px split tracks it for
			// real users).
			{
				rel: 'preload',
				as: 'image',
				href: ASSETS.heroBackground,
				media: '(min-width: 768px)',
				fetchPriority: 'high',
			},
			{
				rel: 'preload',
				as: 'image',
				href: ASSETS.heroBackgroundMobile,
				media: '(max-width: 767px)',
				fetchPriority: 'high',
			},
		],
		// WebPage + FinancialProduct + FAQPage JSON-LD (structured-data.ts) for
		// rich-result eligibility. Pre-escaped (`<`→<) so FAQ text can't break
		// out of the script block. SSR'd → crawlers see it in the initial HTML.
		scripts: [
			// Pin color-scheme: light for this route (runs after the shell's adaptive
			// theme script, so OS dark mode can't tint the light-only CBCC page). See
			// force-light-scheme.tsx; the <ForceLightScheme/> component covers SPA nav.
			{
				children: forceLightSchemeScript,
			},
			{
				type: 'application/ld+json',
				children: cbccStructuredDataJson(),
			},
		],
	}),
	component: TravelCreditCardRoute,
	// Opt this route into SSR (app default is SPA: start.ts → defaultSsr:false)
	// for SEO + first-paint performance.
	ssr: true,
	// Fetch the per-user CVP server-side so `status`/`userFlow` are known on
	// first paint (no marketing→manage flip for approved users). Gated on the
	// isomorphic auth: request-scoped server auth on SSR, the browser singleton
	// on client nav — both `isLoggedIn()` off the session cookies, no raw cookie
	// access. Logged-out users (and crawlers) get the generic marketing HTML. The
	// shell `fetch` injects the Bearer token from cookies on both server and
	// client, so this also re-runs correctly on the post-login
	// `router.invalidate()` the apply CTA fires.
	// Never throws: a failure (incl. a stale-`at` 401) falls back to marketing.
	loader: async () => {
		// Device is resolved HERE (not at render) so the value that shaped the
		// SSR HTML rides the dehydrated loader data — hydration reuses the
		// server's answer verbatim (no variant flip). Client-side navigations
		// re-run this in the browser, where it reads navigator.userAgent — the
		// same client the server classified. See constants/device.ts.
		const device = resolveDevice();
		const auth = await getAuth();
		if (!auth.isLoggedIn()) return { device, screens: null, userFlow: null };
		try {
			const { data } = await fetchCvp();
			const screens = data.screenData;
			return { device, screens, userFlow: resolveUserFlow(screens) };
		} catch {
			return { device, screens: null, userFlow: null };
		}
	},
});

function TravelCreditCardRoute() {
	const { device } = Route.useLoaderData();
	return (
		<>
			<ForceLightScheme />
			<TravelCreditCardLayout device={device} />
		</>
	);
}
